import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import Reviews from "../components/reviews";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { pageView } from "../utils/analytics";

import footProffIcon from "../assets/img/foot-icon-college.jpg";
import hcpcIcon from "../assets/img/hcpc.jpg";
import anneIcon from "../assets/img/anne.jpg";
import biomechanics from "../assets/img/biomechanics-assessment.jpg";
import diabetic from "../assets/img/diabetic-foot-assessment.jpg";
import gait from "../assets/img/gait-analysis.jpg";
import general from "../assets/img/general-podiatry.jpg";
import orthotic from "../assets/img/orthotic.jpg";
import plantar from "../assets/img/plantar-fascitiis.jpg";
import pressurePlate from "../assets/img/pressure-plate-analysis.jpg";
import ultrasound from "../assets/img/ultrasound.jpg";
import collage from "../assets/img/foot-clinic-collage.png";
import waxBath from "../assets/img/wax-bath.png";
import fungalNail from "../assets/img/fungal-nail-test.jpeg";

/**
 * FootClinic component
 *
 * @param {object} props
 */
const FootClinic = () => {
  useEffect(() => pageView());

  return (
    <React.Fragment>
      <Helmet>
        <title>The Moffat Foot Clinic - The Moffat Clinic</title>
        <meta
          name="description"
          content="Anne Bloomer. B.Sc.; D.Pod.M.; M.C.Pod. Anne has over 30 years experience in both the NHS and private practice. She has a particular interest in diabetes having been a diabetes specialist in the NHS for over 10 years."
        />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <section className="gel-wrap testimonies gs-u-align-center spacing-slice--large">
        <div className="spacing-slice--large spacing-slice--large--bottom">
          <h2 className="gs-u-mb++">Our Services</h2>
          <ul className="services__grid">
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${general})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">General Podiatry</b>
                <p className="gs-u-mb+">
                  Treatment of corns, callous, ingrown toenails & a toenail
                  cutting service.
                </p>
                <NavLink
                  to="/foot-clinic/services/general-podiatry"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${plantar})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Plantar Fascitiis Treatment{" "}
                </b>
                <p className="gs-u-mb+">
                  Plantar Fasciitis is severe pain typically found on the heel
                  and/or arch area. Treatment can include heat
                  therapy/ultrasound, strapping and targeted exercises.
                </p>
                <NavLink
                  to="/foot-clinic/services/plantar-fasciitis"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${diabetic})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Diabetic Foot Assessment
                </b>
                <p className="gs-u-mb+">
                  Annual review of circulation, sensation & foot
                  structure/function to determine possible effects of diabetes
                  on your feet.
                </p>
                <NavLink
                  to="/foot-clinic/services/diabetic-foot-care"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${orthotic})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">Orthotics</b>
                <p className="gs-u-mb+">
                  Insoles manufactured to reduce pressure on painful areas,
                  adjust gait or provide cushioning to your feet. Each
                  individually prescribed to your needs.
                </p>
                <NavLink
                  to="/foot-clinic/services/orthotics"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${ultrasound})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Ultrasound therapy
                </b>
                <p className="gs-u-mb+">
                  This is a painless high frequency treatment used to treat deep
                  tissue injuries and so speed your healing process.
                </p>
                <NavLink
                  to="/foot-clinic/services/ultrasound-therapy"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${biomechanics})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Biomechanical assessment
                </b>
                <p className="gs-u-mb+">
                  This is a way of diagnosing any imbalance or weakness in your
                  feet & leg joints.
                </p>
                <NavLink
                  to="/foot-clinic/services/biomechanical-assessment"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${gait})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Video Gait Analysis
                </b>
                <p className="gs-u-mb+">
                  Video recording of your walking/running gait to determine
                  abnormalities and help adjust these. This includes detailed
                  printed analysis & advice.
                </p>
                <NavLink
                  to="/foot-clinic/services/video-gait-analysis"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${pressurePlate})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Pressure Plate Analysis
                </b>
                <p className="gs-u-mb+">
                  A device used to produce a 3D image detailing pressures
                  exerted by your foot when walking and standing.
                </p>
                <NavLink
                  to="/foot-clinic/services/pressure-plate-analysis"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image"
                style={{ backgroundImage: `url(${waxBath})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Paraffin Wax Bath Therapy
                </b>
                <p className="gs-u-mb+">
                  Paraffin wax is an effective way of applying heat to joints
                  and connective tissues. It is mainly used on hands and feet.
                </p>
                <NavLink
                  to="/foot-clinic/services/paraffin-wax-bath"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
            <li className="services__item">
              <div
                className="services__image "
                style={{ backgroundImage: `url(${fungalNail})` }}
              ></div>
              <div className="services__item_content">
                <b className="gs-u-display-block gs-u-mb+">
                  Dermatophyte Fungal Infection Testing
                </b>
                <p className="gs-u-mb+">
                  The test works by a process known as immune-chromotography,
                  which is also found in a pregnancy test. It contains an
                  antibody that specifically reacts with a chemical found in the
                  dermatophyte infection which causes most fungal nail
                  infections.
                </p>
                <NavLink
                  to="/foot-clinic/services/fungal-testing"
                  className="button button--secondary services__cta gs-u-display-inline-flex gs-u-align-items-center"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="gs-u-mr" />
                  Find out more
                </NavLink>
              </div>
            </li>
          </ul>
          <div className="gs-u-mt++">
            <NavLink
              className="button gs-u-pv+ gs-u-ph++ nav__item"
              to="/foot-clinic/booking"
            >
              <FontAwesomeIcon icon={faCalendarAlt} className="gs-u-mr@l" />{" "}
              Book now
            </NavLink>
          </div>
        </div>
      </section>
      <section className="gel-wrap gs-u-align-center spacing-slice--large spacing-slice--large--bottom">
        <figure className="gs-u-display-inline-block gs-u-p+ gs-u-pt-alt+ gel-7/12 gs-u-mr++@m gel-3/12@m gel-3/12@l gel-3/12@xl gel-2/12@xxl gs-u-align-middle">
          <img src={hcpcIcon} alt="" />
        </figure>
        <figure className="gs-u-display-inline-block gs-u-p+ gs-u-pt-alt+ gel-8/12 gs-u-ml++@m gel-5/12@m gel-4/12@l gel-4/12@xl gel-3/12@xxl gs-u-align-middle">
          <img src={footProffIcon} alt="" />
        </figure>
      </section>
      <section className="section--dark spacing-slice--large spacing-slice--large--bottom">
        <div className="gel-wrap padding-slice--large padding-slice--large--bottom">
          <ul className="gel-layout">
            <li className="gel-layout__item gel-1/2@l gs-u-align-center gel-1/2@l gs-u-mb++ gs-u-mb0@l">
              <figure className="gel-6/12 gs-u-display-inline-block">
                <img className="image--rounded" src={anneIcon} alt="" />
              </figure>
            </li>
            <li className="gel-layout__item gel-1/2@l gs-u-mt++@xxl">
              <h2 className="gs-u-mb++">About The Moffat Foot Clinic</h2>
              <p className="text--large gs-u-mb">
                <b>Anne Bloomer. B.Sc.; D.Pod.M.; M.C.Pod</b>
              </p>
              <p className="text--large gs-u-mb++">
                Anne has over 30 years experience as a podiatrist in both the
                NHS and private practice. During her time in the health service
                she spent almost 10 years specialising in diabetes and still has
                a keen interest in this condition. She also worked as a clinical
                tutor to Podiatry undergraduate students at the Queen Elizabeth
                Hospital in Glasgow and finally in a management post in NHS
                Lanarkshire.
              </p>
              <p className="text--large gs-u-mb++">
                On leaving the NHS, Anne opened her first clinic in Moffat in
                2016. Both she and Paula always had a close working
                relationship, often treating the same patient giving quicker
                more effective results. They decided to join forces and opened
                The Moffat Clinic in November 2019. The clinic has been very
                well received and both therapists look forward to getting you
                back to better health and complete fitness - from head to toe.
              </p>
              <p className="text--large gs-u-mb++">
                Both our Podiatrist and Therapist have an excellent working
                relationship with GP practices & should further specialist
                assessment be required this can be facilitated.
              </p>
            </li>
          </ul>
        </div>
      </section>
      <section className="gel-wrap testimonies gs-u-align-center spacing-slice--large--bottom">
        <h2 className="gs-u-mb++">Reviews</h2>
        <Reviews
          reviews={[
            {
              text: "After seeing several different podiatrists and finding little relief, I had an appointment with Anne. Thoroughly impressive and professional, Anne provided some excellent treatment and advice that no-one had ever done before. Thank you Anne.",
            },
            {
              text: "After suffering for weeks with shin splints I booked an appointment with Anne. One gait analysis later, a thorough assessment and some helpful advice, I'm back running and no longer experiencing any pain. Glad to be back out running.",
            },
            {
              text: "After years of pain and many podiatrists later I found the Moffat Foot Clinic. One treatment here and I can't believe the relief.",
            },
            {
              text: "While on holiday recently in Moffat, I visited the clinic. Best foot treatment I've ever had, thanks Anne! Wish I lived closer.....",
            },
          ]}
        />
      </section>
      <section className="gel-wrap gel-wrap-small testimonies gs-u-align-center">
        <img className="spacing-slice--large--bottom" src={collage} alt="" />
      </section>
    </React.Fragment>
  );
};

export default FootClinic;
